import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, User, Phone, AlertCircle, Shield, ShieldCheck, ShieldOff, Lock, Loader2, Sun, Moon, Monitor, KeyRound, Copy, CheckCircle2 } from 'lucide-react';
import { useTheme } from 'next-themes';
import { QRCodeSVG } from 'qrcode.react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Alert, AlertDescription } from '@/app/components/ui/alert';
import { toast } from '@/app/components/ui/simple-toaster';
import { Skeleton } from '@/app/components/ui/skeleton';
import { Badge } from '@/app/components/ui/badge';
import { useProfile, useUpdateProfile } from '@/hooks/use-profile';
import { useAuth } from '@/contexts/auth-context';
import { NotificationPreferencesCard } from './notification-preferences-card';
import { useSetSigningPin, useClearSigningPin } from '@/hooks/use-settings';
import { apiClient } from '@/lib/api-client';

function getPasswordStrength(password: string): { score: number; label: string; color: string } {
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;

  if (score <= 1) return { score, label: 'Weak', color: 'bg-red-500' };
  if (score <= 2) return { score, label: 'Fair', color: 'bg-orange-500' };
  if (score <= 3) return { score, label: 'Good', color: 'bg-yellow-500' };
  if (score <= 4) return { score, label: 'Strong', color: 'bg-green-500' };
  return { score, label: 'Very Strong', color: 'bg-green-600' };
}

function ChangePasswordCard() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const strength = useMemo(() => getPasswordStrength(newPassword), [newPassword]);

  const handleChangePassword = async () => {
    setError(null);

    if (!currentPassword) {
      setError('Current password is required.');
      return;
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);
    try {
      await apiClient.post('/api/v1/auth/change-password/', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success('Password Changed', {
        description: 'Your password has been updated. Please sign in again.',
      });
      if (await logout()) navigate('/login', { replace: true });
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: Record<string, unknown> } };
      const respStatus = axiosErr?.response?.status;
      const respData = axiosErr?.response?.data;
      if (respStatus === 403) {
        setError('Something went wrong. Please refresh the page and try again.');
      } else if (respData) {
        const messages: string[] = [];
        for (const value of Object.values(respData)) {
          if (Array.isArray(value)) {
            messages.push(...value.map(String));
          } else if (typeof value === 'string') {
            messages.push(value);
          }
        }
        setError(messages.length > 0 ? messages.join(' ') : 'Failed to change password.');
      } else {
        setError('Failed to change password. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Lock className="h-5 w-5" />
          Change Password
        </CardTitle>
        <CardDescription>
          Update your password to keep your account secure
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div className="space-y-2">
          <Label htmlFor="current-password">Current Password</Label>
          <Input
            id="current-password"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            disabled={isSubmitting}
            className="max-w-sm"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="new-password">New Password</Label>
          <Input
            id="new-password"
            type="password"
            placeholder="At least 8 characters"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={isSubmitting}
            className="max-w-sm"
          />
          {newPassword && (
            <div className="space-y-1 max-w-sm">
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((level) => (
                  <div
                    key={level}
                    className={`h-1.5 flex-1 rounded-full ${
                      level <= strength.score ? strength.color : 'bg-gray-200'
                    }`}
                  />
                ))}
              </div>
              <p className="text-xs text-gray-500 dark:text-muted-foreground">Strength: {strength.label}</p>
            </div>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm-password">Confirm New Password</Label>
          <Input
            id="confirm-password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={isSubmitting}
            className="max-w-sm"
          />
        </div>
        <Button
          onClick={handleChangePassword}
          disabled={isSubmitting || !currentPassword || !newPassword || !confirmPassword}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Changing...
            </>
          ) : (
            'Change Password'
          )}
        </Button>
      </CardContent>
    </Card>
  );
}

function SigningPinCard() {
  const { user } = useAuth();
  const setPinMutation = useSetSigningPin();
  const clearPinMutation = useClearSigningPin();
  const [pinValue, setPinValue] = useState('');
  const [showPinForm, setShowPinForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localPinOverride, setLocalPinOverride] = useState<boolean | null>(null);
  const hasPin = localPinOverride ?? (user?.has_signing_pin ?? false);

  const handleSetPin = async () => {
    if (!user) return;
    setError(null);
    try {
      await setPinMutation.mutateAsync({ userId: user.id, pin: pinValue });
      toast.success('Signing PIN Set', { description: 'Your kiosk signing PIN has been configured.' });
      setLocalPinOverride(true);
      setPinValue('');
      setShowPinForm(false);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr?.response?.data?.error || 'Failed to set signing PIN.');
    }
  };

  const handleClearPin = async () => {
    if (!user) return;
    setError(null);
    try {
      await clearPinMutation.mutateAsync(user.id);
      toast.success('Signing PIN Cleared', { description: 'Your kiosk signing PIN has been removed.' });
      setLocalPinOverride(false);
    } catch {
      setError('Failed to clear signing PIN.');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="h-5 w-5" />
          Signing PIN
        </CardTitle>
        <CardDescription>
          A 6-digit PIN used for kiosk JSA signing on shared devices. This is not your login password.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-600 dark:text-muted-foreground">Status:</span>
          {hasPin ? (
            <Badge variant="default">PIN configured</Badge>
          ) : (
            <Badge variant="secondary">No PIN set</Badge>
          )}
        </div>

        {showPinForm ? (
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="signing-pin">{hasPin ? 'New 6-Digit PIN' : '6-Digit PIN'}</Label>
              <Input
                id="signing-pin"
                type="tel"
                inputMode="numeric"
                maxLength={6}
                pattern="[0-9]{6}"
                autoComplete="off"
                value={pinValue}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setPinValue(v);
                }}
                placeholder="Enter 6 digits"
                className="max-w-[200px]"
                disabled={setPinMutation.isPending}
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleSetPin}
                disabled={pinValue.length !== 6 || setPinMutation.isPending}
                size="sm"
              >
                {setPinMutation.isPending ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</>
                ) : (
                  hasPin ? 'Update PIN' : 'Set PIN'
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setShowPinForm(false); setPinValue(''); setError(null); }}
                disabled={setPinMutation.isPending}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPinForm(true)}
            >
              {hasPin ? 'Change Signing PIN' : 'Set Signing PIN'}
            </Button>
            {hasPin && (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleClearPin}
                disabled={clearPinMutation.isPending}
              >
                {clearPinMutation.isPending ? 'Clearing...' : 'Clear PIN'}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// MFA status types
// ---------------------------------------------------------------------------

interface MFAStatusResponse {
  mfa_enabled: boolean;
  mfa_required: boolean;
  backup_codes_remaining: number;
}

interface MFAEnrollResponse {
  secret: string;
  provisioning_uri: string;
  issuer: string;
}

interface MFAEnrollConfirmResponse {
  detail: string;
  backup_codes: string[];
}

type MFAStep = 'idle' | 'qr' | 'backup-codes' | 'disable' | 'regenerate';

interface TwoFactorCardProps {
  forced?: boolean;
  required?: boolean;
  onEnrollmentComplete?: () => void;
}

export function TwoFactorCard({ forced = false, required = false, onEnrollmentComplete }: TwoFactorCardProps) {
  const [mfaStatus, setMfaStatus] = useState<MFAStatusResponse | null>(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  const [step, setStep] = useState<MFAStep>('idle');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Enrollment state
  const [enrollData, setEnrollData] = useState<MFAEnrollResponse | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [copiedCodes, setCopiedCodes] = useState(false);

  // Disable / regenerate state
  const [confirmCode, setConfirmCode] = useState('');

  const fetchStatus = useCallback(async () => {
    try {
      const res = await apiClient.get<MFAStatusResponse>('/api/v1/auth/mfa/status/');
      setMfaStatus(res.data);
    } catch {
      setMfaStatus(null);
    } finally {
      setIsLoadingStatus(false);
    }
  }, []);

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const resetFlow = () => {
    setStep('idle');
    setError(null);
    setEnrollData(null);
    setTotpCode('');
    setConfirmCode('');
    setBackupCodes([]);
    setCopiedCodes(false);
  };

  const handleStartEnroll = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      const res = await apiClient.post<MFAEnrollResponse>('/api/v1/auth/mfa/enroll/');
      setEnrollData(res.data);
      setStep('qr');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      setError(axiosErr?.response?.data?.detail || 'Failed to start MFA enrollment.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmEnroll = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      const res = await apiClient.post<MFAEnrollConfirmResponse>('/api/v1/auth/mfa/enroll/confirm/', {
        totp_code: totpCode,
      });
      setBackupCodes(res.data.backup_codes);
      setStep('backup-codes');
      toast.success('MFA Enabled', { description: 'Two-factor authentication is now active.' });
      await fetchStatus();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      setError(axiosErr?.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDisable = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      await apiClient.post('/api/v1/auth/mfa/disable/', { totp_code: confirmCode });
      toast.success('MFA Disabled', { description: 'Two-factor authentication has been removed.' });
      resetFlow();
      await fetchStatus();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      setError(axiosErr?.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegenerate = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      const res = await apiClient.post<MFAEnrollConfirmResponse>('/api/v1/auth/mfa/backup-codes/regenerate/', {
        totp_code: confirmCode,
      });
      setBackupCodes(res.data.backup_codes);
      setStep('backup-codes');
      toast.success('Backup Codes Regenerated', { description: 'Your old codes are no longer valid.' });
      await fetchStatus();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      setError(axiosErr?.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyBackupCodes = async () => {
    try {
      await navigator.clipboard.writeText(backupCodes.join('\n'));
      setCopiedCodes(true);
      setTimeout(() => setCopiedCodes(false), 2000);
    } catch {
      toast.error('Copy Failed', { description: 'Please manually copy the codes.' });
    }
  };

  if (isLoadingStatus) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-72 mt-1" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-8 w-32" />
        </CardContent>
      </Card>
    );
  }

  const isEnabled = mfaStatus?.mfa_enabled ?? false;
  const isRequired = required || (mfaStatus?.mfa_required ?? false);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5" />
          Two-Factor Authentication
        </CardTitle>
        <CardDescription>
          {forced
            ? 'Your organization requires a TOTP authenticator before you can continue'
            : required
              ? 'MFA is required for platform administrator accounts'
              : isRequired
                ? 'Your organization requires MFA for your role'
            : 'Add an extra layer of security with a TOTP authenticator app'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Status row */}
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-600 dark:text-muted-foreground">Status:</span>
          {isEnabled ? (
            <Badge variant="default">Enabled</Badge>
          ) : (
            <Badge variant="secondary">Disabled</Badge>
          )}
          {isEnabled && mfaStatus && (
            <span className="text-xs text-gray-500 dark:text-muted-foreground">
              {mfaStatus.backup_codes_remaining} backup code{mfaStatus.backup_codes_remaining !== 1 ? 's' : ''} remaining
            </span>
          )}
        </div>

        {/* Idle state — show action buttons */}
        {step === 'idle' && (
          <div className="flex flex-wrap gap-2">
            {!isEnabled ? (
              <Button
                variant="outline"
                size="sm"
                onClick={handleStartEnroll}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Setting up...</>
                ) : (
                  'Enable MFA'
                )}
              </Button>
            ) : (
              forced ? (
                <Button size="sm" onClick={onEnrollmentComplete}>
                  Continue
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setStep('regenerate'); setError(null); setConfirmCode(''); }}
                  >
                    Regenerate Backup Codes
                  </Button>
                  {!isRequired && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => { setStep('disable'); setError(null); setConfirmCode(''); }}
                    >
                      Disable MFA
                    </Button>
                  )}
                </>
              )
            )}
          </div>
        )}

        {/* QR code enrollment step */}
        {step === 'qr' && enrollData && (
          <div className="space-y-4">
            <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
              <Shield className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-blue-800 dark:text-blue-300">
                Scan this QR code with your authenticator app (e.g. Google Authenticator, Authy, 1Password).
              </p>
            </div>
            <div className="flex justify-center p-4 bg-white rounded-lg border">
              <QRCodeSVG value={enrollData.provisioning_uri} size={200} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-gray-500 dark:text-muted-foreground">Or enter this secret manually:</Label>
              <code className="block text-xs font-mono bg-gray-100 dark:bg-secondary p-2 rounded select-all break-all">
                {enrollData.secret}
              </code>
            </div>
            <div className="space-y-2">
              <Label htmlFor="mfa-verify-code">Verification Code</Label>
              <Input
                id="mfa-verify-code"
                type="text"
                inputMode="numeric"
                maxLength={6}
                autoComplete="one-time-code"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit code"
                className="max-w-[200px]"
                disabled={isSubmitting}
              />
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleConfirmEnroll}
                disabled={totpCode.length !== 6 || isSubmitting}
              >
                {isSubmitting ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Verifying...</>
                ) : (
                  'Verify & Enable'
                )}
              </Button>
              <Button variant="outline" size="sm" onClick={resetFlow} disabled={isSubmitting}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Backup codes display (after enrollment or regeneration) */}
        {step === 'backup-codes' && backupCodes.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
              <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-800 dark:text-amber-300">
                Save these backup codes in a safe place. Each code can only be used once. They will not be shown again.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 p-3 bg-gray-50 dark:bg-secondary rounded-lg font-mono text-sm">
              {backupCodes.map((code) => (
                <span key={code}>{code}</span>
              ))}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleCopyBackupCodes}>
                {copiedCodes ? (
                  <><CheckCircle2 className="mr-2 h-4 w-4" />Copied!</>
                ) : (
                  <><Copy className="mr-2 h-4 w-4" />Copy Codes</>
                )}
              </Button>
              <Button size="sm" onClick={forced ? onEnrollmentComplete : resetFlow}>
                {forced ? 'Continue' : 'Done'}
              </Button>
            </div>
          </div>
        )}

        {/* Disable MFA confirmation */}
        {step === 'disable' && (
          <div className="space-y-3">
            <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-950/30 rounded-lg">
              <ShieldOff className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-800 dark:text-red-300">
                Enter your current authenticator code to confirm disabling MFA.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="mfa-disable-code">Authentication Code</Label>
              <Input
                id="mfa-disable-code"
                type="text"
                inputMode="numeric"
                maxLength={6}
                autoComplete="one-time-code"
                value={confirmCode}
                onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit code"
                className="max-w-[200px]"
                disabled={isSubmitting}
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDisable}
                disabled={confirmCode.length !== 6 || isSubmitting}
              >
                {isSubmitting ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Disabling...</>
                ) : (
                  'Confirm Disable'
                )}
              </Button>
              <Button variant="outline" size="sm" onClick={resetFlow} disabled={isSubmitting}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Regenerate backup codes confirmation */}
        {step === 'regenerate' && (
          <div className="space-y-3">
            <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
              <Shield className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-blue-800 dark:text-blue-300">
                Enter your current authenticator code to regenerate backup codes. Existing codes will be invalidated.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="mfa-regen-code">Authentication Code</Label>
              <Input
                id="mfa-regen-code"
                type="text"
                inputMode="numeric"
                maxLength={6}
                autoComplete="one-time-code"
                value={confirmCode}
                onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit code"
                className="max-w-[200px]"
                disabled={isSubmitting}
              />
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleRegenerate}
                disabled={confirmCode.length !== 6 || isSubmitting}
              >
                {isSubmitting ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Regenerating...</>
                ) : (
                  'Regenerate Codes'
                )}
              </Button>
              <Button variant="outline" size="sm" onClick={resetFlow} disabled={isSubmitting}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const THEME_OPTIONS = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
] as const;

function AppearanceCard() {
  const { theme, setTheme } = useTheme();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sun className="h-5 w-5" />
          Appearance
        </CardTitle>
        <CardDescription>
          Choose how H3ight looks to you. Select a theme preference.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex gap-2">
          {THEME_OPTIONS.map(({ value, label, icon: Icon }) => (
            <Button
              key={value}
              variant={theme === value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setTheme(value)}
              className="flex items-center gap-2"
            >
              <Icon className="h-4 w-4" />
              {label}
            </Button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export function MyProfilePage() {
  const { refreshUser } = useAuth();
  const { data: profile, isLoading, error } = useProfile();
  const updateProfile = useUpdateProfile();

  const [phone, setPhone] = useState('');
  const [emergencyContact, setEmergencyContact] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (profile) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPhone(profile.phone || '');
      setEmergencyContact(profile.emergency_contact || '');
    }
  }, [profile]);

  useEffect(() => {
    if (profile) {
      const phoneChanged = phone !== (profile.phone || '');
      const emergencyChanged = emergencyContact !== (profile.emergency_contact || '');
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setHasChanges(phoneChanged || emergencyChanged);
    }
  }, [phone, emergencyContact, profile]);

  const handleSave = async () => {
    try {
      await updateProfile.mutateAsync({
        phone,
        emergency_contact: emergencyContact,
      });
      await refreshUser();
      toast.success('Profile Updated', {
        description: 'Your profile has been updated successfully.',
      });
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: Record<string, string[]> } };
      const fieldErrors = axiosError?.response?.data;
      if (fieldErrors && typeof fieldErrors === 'object') {
        const messages = Object.entries(fieldErrors)
          .map(([field, errors]) => {
            const label = field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
            const msgs = Array.isArray(errors) ? errors.join(', ') : String(errors);
            return `${label}: ${msgs}`;
          })
          .join('\n');
        if (messages) {
          toast.error('Validation Error', { description: messages });
          return;
        }
      }
      toast.error('Error', {
        description: 'Failed to update profile. Please try again.',
      });
    }
  };

  const handleReset = () => {
    if (profile) {
      setPhone(profile.phone || '');
      setEmergencyContact(profile.emergency_contact || '');
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-8 w-1/4" />
        <Skeleton className="h-48" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-900">Error Loading Profile</h3>
                <p className="text-sm text-red-800 mt-1">
                  Failed to load your profile. Please try refreshing the page.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-foreground">My Profile</h1>
          <p className="text-gray-600 dark:text-muted-foreground mt-1">View and update your personal information</p>
        </div>
        <div className="flex gap-2">
          {hasChanges && (
            <Button variant="outline" onClick={handleReset}>
              Reset
            </Button>
          )}
          <Button onClick={handleSave} disabled={!hasChanges || updateProfile.isPending}>
            <Save className="h-4 w-4 mr-2" />
            {updateProfile.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Read-only account info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Account Information
          </CardTitle>
          <CardDescription>
            These fields are managed by your organization administrator
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-gray-500 dark:text-muted-foreground">Username</Label>
              <p className="text-sm font-medium text-gray-900 dark:text-foreground">{profile?.username}</p>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-500 dark:text-muted-foreground">Email</Label>
              <p className="text-sm font-medium text-gray-900 dark:text-foreground">{profile?.email}</p>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-500 dark:text-muted-foreground">Job Title</Label>
              <p className="text-sm font-medium text-gray-900 dark:text-foreground">{profile?.job_title || 'Not set'}</p>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-500 dark:text-muted-foreground">Department</Label>
              <p className="text-sm font-medium text-gray-900 dark:text-foreground">{profile?.department || 'Not set'}</p>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-500 dark:text-muted-foreground">Role</Label>
              <p className="text-sm font-medium text-gray-900 dark:text-foreground">{profile?.role || 'Not assigned'}</p>
            </div>
          </div>
          <div className="flex items-start gap-2 mt-2 p-3 bg-gray-50 dark:bg-secondary rounded-lg">
            <Shield className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-gray-500 dark:text-muted-foreground">
              Job title, department, and role are managed by your administrator. Contact them to request changes.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Editable contact info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5" />
            Contact Information
          </CardTitle>
          <CardDescription>
            Update your phone number and emergency contact details
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              type="tel"
              placeholder="e.g., +1 555-0123"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="max-w-sm"
            />
            <p className="text-sm text-gray-500 dark:text-muted-foreground">
              Your direct contact number for work-related communications
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="emergency-contact">Emergency Contact</Label>
            <Input
              id="emergency-contact"
              type="text"
              placeholder="e.g., Jane Doe — +1 555-0456 (Spouse)"
              value={emergencyContact}
              onChange={(e) => setEmergencyContact(e.target.value)}
              className="max-w-lg"
            />
            <p className="text-sm text-gray-500 dark:text-muted-foreground">
              Name, phone number, and relationship of your emergency contact
            </p>
          </div>
        </CardContent>
      </Card>

      <AppearanceCard />

      <NotificationPreferencesCard />

      {/* Security section */}
      <div className="space-y-1 pt-2">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-foreground flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Security
        </h2>
        <p className="text-sm text-gray-500 dark:text-muted-foreground">
          Manage your account security settings
        </p>
      </div>

      <ChangePasswordCard />

      <TwoFactorCard />

      <SigningPinCard />

      {/* Bottom save bar */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        {hasChanges && (
          <Button variant="outline" onClick={handleReset}>
            Reset
          </Button>
        )}
        <Button onClick={handleSave} disabled={!hasChanges || updateProfile.isPending}>
          <Save className="h-4 w-4 mr-2" />
          {updateProfile.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}
